/**
 * Phase 8 — pricing an outreach offer from the comp database.
 *
 * The LOI Sender takes a single typed figure today and multiplies it by the
 * building's RBA. Nothing stands behind the number. With the comp database in
 * place it can be derived, and — more importantly — shown with its working:
 *
 *     market rent      signed lease comps within the radius, filtered to the
 *                      product type and size band, weighted toward recent
 *                      signings
 *     expense load     OpEx per foot from comparable properties in the same
 *                      submarket, out of our own line items rather than a rule
 *                      of thumb
 *     implied NOI      market rent across the RBA, less vacancy and credit
 *                      loss, less any non-recovered expense
 *     target yield     going-in cap rates from our sale comps in the same
 *                      submarket, or a hurdle the user sets
 *     offer per foot   NOI over the yield, divided by RBA, then cross-checked
 *                      against what has actually traded in the radius
 *
 * THE HONESTY GUARD. Our coverage is dense in the submarkets we have
 * underwritten and thin everywhere else. An outreach target in a submarket
 * where we hold three comps from 2023 returns `insufficient evidence` rather
 * than a confident figure — otherwise the tool becomes a machine for dressing
 * up guesses, which is worse than the typed number it replaces. Every stage
 * below can independently fail that test, and the failure is reported rather
 * than defaulted around.
 */

import { supabase, haversineMiles, plausibleRent } from "../supabase";

const MILES_PER_DEG_LAT = 69.0;

// What a reasonable industrial sale clears. Same discipline as the rent band:
// a figure outside this cannot be a price per foot, so it cannot set one.
const SALE_PSF_FLOOR = 20;
const SALE_PSF_CEILING = 600;

// Below these, we say so instead of answering.
const MIN_RENT_COMPS = 4;
const MIN_SALE_COMPS = 3;

export function sizeBandOf(sf) {
  const n = Number(sf) || 0;
  if (!n) return null;
  if (n < 5000) return "< 5k";
  if (n < 15000) return "5-15k";
  if (n < 50000) return "15-50k";
  return "50k+";
}

/** Comps of either kind within a radius, in one bounding-box query. */
async function compsWithin(lat, lng, radiusMiles, type) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
  const dLat = radiusMiles / MILES_PER_DEG_LAT;
  const dLng = radiusMiles / (MILES_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180) || 1);
  const { data, error } = await supabase
    .from("comps").select("*")
    .eq("type", type)
    .gte("latitude", lat - dLat).lte("latitude", lat + dLat)
    .gte("longitude", lng - dLng).lte("longitude", lng + dLng)
    .limit(600);
  if (error) { console.error("compsWithin:", error.message); return []; }
  return (data || [])
    .map((c) => ({ ...c, miles: haversineMiles(lat, lng, c.latitude, c.longitude) }))
    .filter((c) => c.miles <= radiusMiles);
}

/** OpEx per foot actually observed near here, from our own line items. */
async function opexPsfNearby(market) {
  if (!market) return { psf: null, n: 0 };
  const { data: deals, error: e1 } = await supabase
    .from("deals").select("id,square_footage,market").eq("market", market);
  if (e1 || !deals?.length) return { psf: null, n: 0 };
  const withSf = deals.filter((d) => Number(d.square_footage) > 0);
  if (!withSf.length) return { psf: null, n: 0 };

  const { data: lines, error: e2 } = await supabase
    .from("opex_lines")
    .select("deal_id,amount_annual,basis,is_subtotal,category")
    .in("deal_id", withSf.map((d) => d.id));
  if (e2 || !lines?.length) return { psf: null, n: 0 };

  // One basis per deal — adding a T12 to a budget would double the expenses.
  const perDeal = {};
  lines.forEach((l) => {
    if (l.is_subtotal || l.category === "Total") return;
    perDeal[l.deal_id] = perDeal[l.deal_id] || {};
    const b = l.basis || "Unknown";
    perDeal[l.deal_id][b] = (perDeal[l.deal_id][b] || 0) + (Number(l.amount_annual) || 0);
  });

  const psfs = [];
  withSf.forEach((d) => {
    const bases = perDeal[d.id] && Object.keys(perDeal[d.id]);
    if (!bases || !bases.length) return;
    const v = perDeal[d.id][bases[0]] / Number(d.square_footage);
    if (v > 0.5 && v < 20) psfs.push(v);      // a plausible industrial load
  });
  if (!psfs.length) return { psf: null, n: 0 };
  psfs.sort((a, b) => a - b);
  return { psf: psfs[Math.floor(psfs.length / 2)], n: psfs.length };   // median
}

/**
 * Price an offer, and return the working.
 *
 * `subject` needs lat/lng and rba; everything else is optional and narrows
 * the comp set rather than being required.
 */
export async function priceOffer(subject, {
  radiusMiles = 3,
  vacancyRate = 0.07,          // vacancy and credit loss together
  targetYield = null,          // overrides the derived going-in cap
  nonRecoveredShare = 0.15,    // share of OpEx the landlord eats on a NNN deal
} = {}) {
  const working = [];
  const fail = (why) => ({ ok: false, verdict: "insufficient evidence", why, working });

  const lat = Number(subject?.lat), lng = Number(subject?.lng);
  const rba = Number(subject?.rba) || 0;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return fail("The property has no coordinate, so nothing can be found near it.");
  }
  if (!rba) return fail("No building area, so an offer cannot be totalled.");

  const band = sizeBandOf(rba);

  // ── 1. market rent ────────────────────────────────────────────────────
  const leases = await compsWithin(lat, lng, radiusMiles, "lease");
  const usable = leases.filter((c) => plausibleRent(c.lease_rate));
  const sameBand = band ? usable.filter((c) => sizeBandOf(c.suite_sf) === band) : [];
  // Prefer the same size band, but fall back rather than return nothing -
  // and say which was used.
  const rentSet = sameBand.length >= MIN_RENT_COMPS ? sameBand : usable;
  if (rentSet.length < MIN_RENT_COMPS) {
    return fail(`Only ${rentSet.length} usable signed lease${rentSet.length === 1 ? "" : "s"} `
      + `within ${radiusMiles} miles. Below ${MIN_RENT_COMPS} we do not have a market rent.`);
  }

  const thisYear = new Date().getFullYear();
  let num = 0, den = 0;
  rentSet.forEach((c) => {
    const yr = Number(String(c.comp_date || "").slice(0, 4));
    const age = yr ? Math.max(0, thisYear - yr) : 4;
    const w = 1 / (1 + age);                   // recent signings weigh more
    num += Number(c.lease_rate) * w; den += w;
  });
  const marketRent = num / den;
  const rates = rentSet.map((c) => Number(c.lease_rate)).sort((a, b) => a - b);
  working.push({
    step: "Market rent",
    value: `$${marketRent.toFixed(2)}/SF`,
    detail: `${rentSet.length} signed lease${rentSet.length === 1 ? "" : "s"} within `
      + `${radiusMiles} mi${sameBand.length >= MIN_RENT_COMPS ? `, ${band} band` : ", all size bands"}`
      + `, ranging $${rates[0].toFixed(2)}–$${rates[rates.length - 1].toFixed(2)}, weighted toward recent signings`,
  });

  // ── 2. expense load ───────────────────────────────────────────────────
  const opex = await opexPsfNearby(subject.market);
  if (opex.psf) {
    working.push({
      step: "Expense load",
      value: `$${opex.psf.toFixed(2)}/SF`,
      detail: `median of ${opex.n} propert${opex.n === 1 ? "y" : "ies"} we hold expenses for in ${subject.market}`,
    });
  } else {
    working.push({
      step: "Expense load",
      value: "not held",
      detail: `No reconciled expenses in ${subject.market || "this market"}; only the landlord's `
        + `non-recovered share is modelled, and that share is an assumption.`,
    });
  }

  // ── 3. implied NOI ────────────────────────────────────────────────────
  const gross = marketRent * rba;
  const lossToVacancy = gross * vacancyRate;
  const landlordExpense = (opex.psf || 0) * rba * nonRecoveredShare;
  const noi = gross - lossToVacancy - landlordExpense;
  working.push({
    step: "Implied NOI",
    value: `$${Math.round(noi).toLocaleString()}`,
    detail: `$${marketRent.toFixed(2)} × ${rba.toLocaleString()} SF, less ${(vacancyRate * 100).toFixed(0)}% `
      + `vacancy and credit loss${opex.psf ? `, less ${(nonRecoveredShare * 100).toFixed(0)}% of `
        + `$${opex.psf.toFixed(2)}/SF expenses as non-recovered` : ""}`,
  });

  // ── 4. target yield ───────────────────────────────────────────────────
  const sales = await compsWithin(lat, lng, radiusMiles, "sale");
  const caps = sales.map((s) => Number(s.cap_rate))
    .filter((v) => Number.isFinite(v) && v > 2 && v < 15).sort((a, b) => a - b);
  let yieldPct = targetYield;
  if (yieldPct) {
    working.push({ step: "Target yield", value: `${yieldPct.toFixed(2)}%`, detail: "your hurdle, not derived" });
  } else if (caps.length >= MIN_SALE_COMPS) {
    yieldPct = caps[Math.floor(caps.length / 2)];
    working.push({
      step: "Target yield",
      value: `${yieldPct.toFixed(2)}%`,
      detail: `median going-in cap from ${caps.length} sale${caps.length === 1 ? "" : "s"} within ${radiusMiles} mi`,
    });
  } else {
    // Cap rates are the figure we have least of: 2 of 447 sale comps carry
    // one, because an OM states a price and a size far more often than it
    // states a yield. Refusing to price without one would make this tool
    // silent almost everywhere, so the traded $/SF becomes the anchor instead
    // and the yield route is reported as unavailable rather than fatal.
    yieldPct = null;
    working.push({
      step: "Target yield",
      value: "not held",
      detail: `${caps.length} of the ${sales.length} sale comps in range state a cap rate. `
        + `Priced off what has traded instead; set a hurdle above to price off yield.`,
    });
  }

  // ── 5. what has actually traded ───────────────────────────────────────
  const traded = sales.map((s) => Number(s.price_psf))
    .filter((v) => Number.isFinite(v) && v >= SALE_PSF_FLOOR && v <= SALE_PSF_CEILING)
    .sort((a, b) => a - b);
  const tradedMedian = traded.length ? traded[Math.floor(traded.length / 2)] : null;
  if (traded.length >= MIN_SALE_COMPS) {
    working.push({
      step: "Traded nearby",
      value: `$${tradedMedian.toFixed(2)}/SF`,
      detail: `median of ${traded.length} sale${traded.length === 1 ? "" : "s"} within ${radiusMiles} mi, `
        + `$${traded[0].toFixed(0)}–$${traded[traded.length - 1].toFixed(0)}/SF`,
    });
  }

  // ── 6. the offer ──────────────────────────────────────────────────────
  // Two routes, and which one leads depends on what the data actually
  // supports. A yield needs cap rates we rarely have; traded $/SF we almost
  // always have. Where both exist they are shown together, because a
  // disagreement between them is the most useful thing on the page.
  let offerPsf = null, value = null, basis = null;
  const byYield = yieldPct ? (noi / (yieldPct / 100)) / rba : null;

  if (byYield && tradedMedian) {
    offerPsf = byYield;
    basis = "NOI over the going-in yield";
    const off = Math.abs(byYield - tradedMedian) / tradedMedian;
    working.push({
      step: "Offer per foot",
      value: `$${byYield.toFixed(2)}/SF`,
      detail: `$${Math.round(noi).toLocaleString()} NOI at ${yieldPct.toFixed(2)}% over ${rba.toLocaleString()} SF`
        + (off > 0.25
          ? ` — but that is ${(off * 100).toFixed(0)}% from the $${tradedMedian.toFixed(2)} median trade, which is a disagreement worth resolving before sending`
          : `, within ${(off * 100).toFixed(0)}% of what has traded`),
    });
  } else if (byYield) {
    offerPsf = byYield;
    basis = "NOI over the going-in yield";
    working.push({
      step: "Offer per foot",
      value: `$${byYield.toFixed(2)}/SF`,
      detail: `$${Math.round(noi).toLocaleString()} NOI at ${yieldPct.toFixed(2)}% over ${rba.toLocaleString()} SF. `
        + `No traded comps in range to check it against.`,
    });
  } else if (tradedMedian) {
    offerPsf = tradedMedian;
    basis = "what has traded nearby";
    const impliedCap = (noi / (tradedMedian * rba)) * 100;
    working.push({
      step: "Offer per foot",
      value: `$${tradedMedian.toFixed(2)}/SF`,
      detail: `taken from what has traded, since no cap rates are held. At this price the `
        + `implied going-in on our NOI is ${impliedCap.toFixed(2)}% — check that against your hurdle.`,
    });
  } else {
    return fail(`No cap rates and no traded prices within ${radiusMiles} miles. There is nothing `
      + `to price against; widen the radius or set a hurdle yourself.`);
  }
  value = offerPsf * rba;

  let sanity = null;
  if (traded.length >= MIN_SALE_COMPS) {
    const lo = traded[0], hi = traded[traded.length - 1];
    const inside = offerPsf >= lo * 0.8 && offerPsf <= hi * 1.2;
    sanity = { lo, hi, n: traded.length, inside };
    if (!inside) {
      working.push({
        step: "Cross-check",
        value: "OUTSIDE the traded range",
        detail: `${traded.length} sales traded $${lo.toFixed(0)}–$${hi.toFixed(0)}/SF and the derived `
          + `$${offerPsf.toFixed(2)} does not sit in it. Treat this as unsafe.`,
      });
    }
  }

  const recentN = rentSet.filter((c) =>
    Number(String(c.comp_date || "").slice(0, 4)) >= thisYear - 2).length;
  const verdict = !sanity?.inside && sanity ? "outside the traded range"
    : rentSet.length >= 10 && recentN >= 4 ? "well supported"
    : rentSet.length >= 6 ? "supported" : "thin";

  return {
    ok: true, verdict, basis,
    offerPsf, totalOffer: value, noi, marketRent, tradedMedian,
    opexPsf: opex.psf, yieldPct, rba,
    evidence: { rentComps: rentSet.length, recentRentComps: recentN, saleComps: sales.length, capComps: caps.length },
    sanity, working,
  };
}
